import {
  MCP_SKILLS_READ_SCOPE,
  MCP_SKILLS_WRITE_SCOPE,
} from "@arti/schemas/mcpScopes";

import type { DashboardMcpTokenCreateResponse } from "../../dashboard-client";

import {
  EMBEDDING_KEY_MISSING_MESSAGE,
  EMBEDDING_KEY_MISSING_TITLE,
} from "../../constants/embeddingReadiness";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { CopyButton } from "../common/CopyButton";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormInput } from "../common/forms/FormInput";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { WizardStepper } from "../common/WizardStepper";
import { McpEndpointTile, McpSummaryCard } from "./McpSummaryTiles";
import {
  SKILL_MESH_CLIENTS,
  SKILL_MESH_TOKEN_MASK,
  SKILL_MESH_WIZARD_STEPS,
  type SkillMeshAuthMethod,
  type SkillMeshClient,
  type SkillMeshClientId,
  type SkillMeshCommand,
  type SkillMeshConnectionRow,
  type SkillMeshConnectionTone,
  type SkillMeshSetupStep,
} from "./skillMeshOnboarding";

const SUBHEADING =
  "font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-700 dark:text-slate-200";
const BODY_SM = "text-sm text-slate-700 dark:text-slate-200";
const BODY_XS = "text-xs text-slate-600 dark:text-slate-300";
const FOOTNOTE =
  "mt-2 font-mono text-[10px] text-slate-500 dark:text-slate-400";
const INNER_BOX =
  "border border-slate-300 bg-white/90 p-3 dark:border-slate-700 dark:bg-slate-950/60";

type ChipTone = "accent" | "danger" | "guarded" | "neutral";

const CHIP_TONES: Record<ChipTone, string> = {
  accent:
    "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200",
  danger:
    "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200",
  guarded:
    "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200",
  neutral:
    "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

function Chip({
  children,
  tone = "neutral",
}: {
  children: string;
  tone?: ChipTone;
}) {
  return (
    <span
      className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${CHIP_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** A radio choice styled like the MCP scope cards. */
function ChoiceCard({
  chips,
  checked,
  description,
  label,
  name,
  onSelect,
}: {
  chips: { label: string; tone: ChipTone }[];
  checked: boolean;
  description: string;
  label: string;
  name: string;
  onSelect: () => void;
}) {
  const toneStyles = checked
    ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-slate-50 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.16)] dark:border-emerald-500/40 dark:from-emerald-500/10 dark:via-slate-950/40 dark:to-slate-900/70"
    : "border-slate-200 bg-white/90 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:hover:border-slate-700 dark:hover:bg-slate-900/60";

  return (
    <label className="relative block cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-emerald-500/60">
      {/* Overlaid on the card rather than sr-only, as in McpTokenScopePicker,
          so focusing it never scrolls the window. */}
      <input
        type="radio"
        name={name}
        aria-label={label}
        checked={checked}
        onChange={onSelect}
        className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
      />
      <div className={`h-full border p-3 transition-colors ${toneStyles}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-slate-900 dark:text-slate-100">
              {label}
            </p>
            <p className={BODY_XS}>{description}</p>
          </div>
          <div
            className={`flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] font-bold ${
              checked
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "border-slate-300 bg-white text-transparent dark:border-slate-600 dark:bg-slate-900"
            }`}
            aria-hidden="true"
          >
            {checked ? "✓" : "·"}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <Chip key={chip.label} tone={chip.tone}>
              {chip.label}
            </Chip>
          ))}
        </div>
      </div>
    </label>
  );
}

/** A command or value with Copy; a token inside it stays masked until shown. */
export function SkillMeshCommandRow({
  command,
  onToggleReveal,
  revealed,
}: {
  command: SkillMeshCommand;
  onToggleReveal: () => void;
  revealed: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
      <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-all border border-slate-200 bg-white/90 px-3 py-2 font-mono text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-200">
        {command.display}
      </pre>
      {command.hasToken && (
        <Button
          variant="outline"
          size="sm"
          aria-pressed={revealed}
          onClick={onToggleReveal}
        >
          {revealed ? "Hide" : "Show"}
        </Button>
      )}
      <CopyButton text={command.copy} />
    </div>
  );
}

export type SkillMeshSearchReadiness =
  | "checking"
  | "hidden"
  | "missing"
  | "ready";

function availabilityBadge(enabled: boolean | null): string {
  if (enabled === null) {
    return "Checking";
  }
  return enabled ? "Enabled" : "Disabled";
}

const SEARCH_TILE: Record<
  Exclude<SkillMeshSearchReadiness, "hidden">,
  { tone: "emerald" | "slate"; value: string }
> = {
  checking: { tone: "slate", value: "Checking" },
  missing: { tone: "slate", value: "Needs a key" },
  ready: { tone: "emerald", value: "Ready" },
};

export function SkillMeshOverviewPanel({
  checking,
  connectionCount,
  enabled,
  endpoint,
  isPlatformAdmin,
  progress,
  search,
}: {
  checking: boolean;
  connectionCount: number;
  enabled: boolean | null;
  endpoint: string;
  isPlatformAdmin: boolean;
  progress: { completedStepIds: ReadonlySet<string>; currentStepId: string };
  search: SkillMeshSearchReadiness;
}) {
  return (
    <PanelSection
      title="Skill Mesh MCP"
      badge={<span>{availabilityBadge(enabled)}</span>}
    >
      <div className="space-y-4">
        <Alert variant="info" title="Shared skill catalog">
          Your organization’s catalog of agent skills. Connect Claude or Codex
          to search, read and publish skills that everyone in your organization
          can reuse. Setup takes about two minutes.
        </Alert>
        {enabled === true && (
          <>
            <div
              className={`grid gap-3 ${
                search === "hidden"
                  ? "lg:grid-cols-[minmax(0,2fr)_repeat(2,minmax(0,1fr))]"
                  : "lg:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]"
              }`}
            >
              <McpEndpointTile endpoint={endpoint} />
              <McpSummaryCard eyebrow="Status" tone="emerald" value="Enabled" />
              {search !== "hidden" && (
                <McpSummaryCard
                  eyebrow="Semantic search"
                  tone={SEARCH_TILE[search].tone}
                  value={SEARCH_TILE[search].value}
                />
              )}
              <McpSummaryCard
                eyebrow="Your connections"
                tone="slate"
                value={String(connectionCount)}
              />
            </div>
            {search === "missing" && (
              <Alert variant="warning" title={EMBEDDING_KEY_MISSING_TITLE}>
                {EMBEDDING_KEY_MISSING_MESSAGE}
              </Alert>
            )}
            <WizardStepper
              ariaLabel="Skill Mesh setup progress"
              steps={SKILL_MESH_WIZARD_STEPS}
              currentStepId={progress.currentStepId}
              completedStepIds={progress.completedStepIds}
            />
          </>
        )}
        {enabled === false && (
          <EmptyState
            title="Skill Mesh isn’t enabled for your organization"
            description="A platform administrator turns it on per organization under Admin → Feature Flags → Skill Mesh. Once it’s on, this page walks you through connecting Claude or Codex."
            action={
              isPlatformAdmin ? (
                <a
                  href="/admin?tab=feature-flags"
                  className="inline-flex items-center justify-center border border-slate-300 bg-transparent px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-600 transition-all duration-200 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                >
                  Open Feature Flags
                </a>
              ) : undefined
            }
          />
        )}
        {enabled === null && checking && (
          <LoadingState message="Checking Skill Mesh availability..." />
        )}
      </div>
    </PanelSection>
  );
}

export function SkillMeshClientPicker({
  onSelect,
  selected,
}: {
  onSelect: (client: SkillMeshClientId) => void;
  selected: SkillMeshClient;
}) {
  return (
    <PanelSection
      title="01 · Choose your client"
      badge={<span>{selected.label}</span>}
    >
      <div className="space-y-4">
        <p className={BODY_SM}>
          Pick the app you want to use Skill Mesh from. You can connect more
          than one; each gets its own revocable connection.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          {SKILL_MESH_CLIENTS.map((client) => (
            <ChoiceCard
              key={client.id}
              name="skill-mesh-client"
              checked={client.id === selected.id}
              label={client.label}
              description={client.description}
              chips={[
                { label: client.kind, tone: "neutral" },
                client.signInOnly
                  ? { label: "Sign in", tone: "accent" }
                  : { label: "Sign in or token", tone: "neutral" },
              ]}
              onSelect={() => onSelect(client.id)}
            />
          ))}
        </div>
      </div>
    </PanelSection>
  );
}

function daysUntil(isoDate: string, now: number): number {
  return Math.ceil((new Date(isoDate).getTime() - now) / (24 * 60 * 60 * 1000));
}

function TokenForm({
  clientLabel,
  creating,
  label,
  onGenerate,
  onLabelChange,
  onWriteChange,
  write,
}: {
  clientLabel: string;
  creating: boolean;
  label: string;
  onGenerate: () => void;
  onLabelChange: (value: string) => void;
  onWriteChange: (write: boolean) => void;
  write: boolean;
}) {
  return (
    <div className="space-y-5 border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
        <FormInput
          aria-label="Label"
          label="Label"
          placeholder={`${clientLabel} · build server`}
          value={label}
          onChange={(event) => onLabelChange(event.target.value)}
          helperText="Name the machine or workflow so you can recognize it when you revoke it."
        />
        <div className="lg:pb-[3px]">
          <Button
            variant="primary"
            onClick={onGenerate}
            loading={creating}
            loadingText="Generating..."
          >
            Generate token
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <p className={SUBHEADING}>Access</p>
            <p className={BODY_XS}>
              Read is enough to search and use skills. Add publish only for
              clients that should create or update shared skills.
            </p>
          </div>
          <Chip>Expires in 30 days</Chip>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <ChoiceCard
            name="skill-mesh-access"
            checked={!write}
            label="Read skills"
            description="Search, read and download shared skills."
            chips={[
              { label: "Read", tone: "neutral" },
              { label: MCP_SKILLS_READ_SCOPE, tone: "neutral" },
            ]}
            onSelect={() => onWriteChange(false)}
          />
          <ChoiceCard
            name="skill-mesh-access"
            checked={write}
            label="Read and publish skills"
            description="Also create and update shared skills for the whole organization."
            chips={[
              { label: "Guarded", tone: "guarded" },
              { label: MCP_SKILLS_WRITE_SCOPE, tone: "neutral" },
            ]}
            onSelect={() => onWriteChange(true)}
          />
        </div>
      </div>
    </div>
  );
}

function IssuedToken({
  now,
  onToggleReveal,
  revealed,
  token,
}: {
  now: number;
  onToggleReveal: () => void;
  revealed: boolean;
  token: DashboardMcpTokenCreateResponse;
}) {
  const publishes = token.scopes.includes(MCP_SKILLS_WRITE_SCOPE);
  const days = daysUntil(token.expires_at, now);
  return (
    <>
      <Alert variant="warning" title="Shown once">
        Copy the token now and keep it in your password manager. ARTI doesn’t
        store it, so it can’t be shown again. If you lose it, revoke it and
        generate a new one.
      </Alert>
      <div className={INNER_BOX}>
        <div className="flex flex-wrap items-center gap-2">
          <p className={SUBHEADING}>Access token</p>
          <Chip tone={publishes ? "guarded" : "neutral"}>
            {publishes ? MCP_SKILLS_WRITE_SCOPE : MCP_SKILLS_READ_SCOPE}
          </Chip>
          <Chip>
            {days === 1 ? "Expires in 1 day" : `Expires in ${days} days`}
          </Chip>
        </div>
        <div className="mt-3">
          <SkillMeshCommandRow
            command={{
              copy: token.token,
              display: revealed ? token.token : SKILL_MESH_TOKEN_MASK,
              hasToken: true,
            }}
            onToggleReveal={onToggleReveal}
            revealed={revealed}
          />
        </div>
        <p className={FOOTNOTE}>
          Copy includes the full token even while it’s hidden.
        </p>
      </div>
    </>
  );
}

export function SkillMeshConnectPanel({
  client,
  creating,
  label,
  method,
  now,
  onGenerate,
  onLabelChange,
  onMethodChange,
  onToggleReveal,
  onWriteChange,
  revealed,
  steps,
  token,
  write,
}: {
  client: SkillMeshClient;
  creating: boolean;
  label: string;
  /** Already resolved: sign-in-only clients always pass "oauth". */
  method: SkillMeshAuthMethod;
  now: number;
  onGenerate: () => void;
  onLabelChange: (value: string) => void;
  onMethodChange: (method: SkillMeshAuthMethod) => void;
  onToggleReveal: () => void;
  onWriteChange: (write: boolean) => void;
  revealed: boolean;
  steps: SkillMeshSetupStep[];
  token: DashboardMcpTokenCreateResponse | null;
  write: boolean;
}) {
  const usesToken = method === "token";
  return (
    <PanelSection
      title={`02 · Connect ${client.label}`}
      badge={<span>{usesToken ? "Access token" : "Sign in"}</span>}
    >
      <div className="space-y-4">
        {!client.signInOnly && (
          <div className="space-y-2">
            <p className={SUBHEADING}>Sign-in method</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={usesToken ? "outline" : "primary"}
                aria-pressed={!usesToken}
                onClick={() => onMethodChange("oauth")}
              >
                Sign in with ARTI
              </Button>
              <Button
                size="sm"
                variant={usesToken ? "primary" : "outline"}
                aria-pressed={usesToken}
                onClick={() => onMethodChange("token")}
              >
                Access token
              </Button>
              <Chip tone="accent">Sign-in recommended</Chip>
            </div>
            <p className={BODY_XS}>
              {usesToken
                ? "For headless machines and CI. Tokens expire after 30 days and are shown once."
                : "Your browser opens ARTI to approve access. No secret is copied or saved in a config file."}
            </p>
          </div>
        )}
        {client.note && (
          <Alert variant="info" title={client.note.title}>
            {client.note.body}
          </Alert>
        )}
        {usesToken && token === null && (
          <TokenForm
            clientLabel={client.label}
            creating={creating}
            label={label}
            onGenerate={onGenerate}
            onLabelChange={onLabelChange}
            onWriteChange={onWriteChange}
            write={write}
          />
        )}
        {usesToken && token !== null && (
          <IssuedToken
            now={now}
            onToggleReveal={onToggleReveal}
            revealed={revealed}
            token={token}
          />
        )}
        {steps.length > 0 ? (
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={step.title} className={INNER_BOX}>
                <div className="space-y-1">
                  <p className={SUBHEADING}>
                    Step {String(index + 1).padStart(2, "0")} · {step.title}
                  </p>
                  <p className={BODY_XS}>{step.body}</p>
                </div>
                {step.command && (
                  <div className="mt-3">
                    <SkillMeshCommandRow
                      command={step.command}
                      onToggleReveal={onToggleReveal}
                      revealed={revealed}
                    />
                  </div>
                )}
                {step.footnote && <p className={FOOTNOTE}>{step.footnote}</p>}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Generate a token first"
            description={`The setup commands for ${client.label} appear here with your token filled in.`}
          />
        )}
      </div>
    </PanelSection>
  );
}

const STATUS_TONES: Record<SkillMeshConnectionTone, ChipTone> = {
  active: "accent",
  expired: "danger",
  idle: "neutral",
  waiting: "guarded",
};

function ConnectionsTable({
  onRevoke,
  pending,
  revokingId,
  rows,
}: {
  onRevoke: (row: SkillMeshConnectionRow) => void;
  /** The single row is a placeholder, so there is nothing to revoke. */
  pending: boolean;
  revokingId: string | null;
  rows: SkillMeshConnectionRow[];
}) {
  return (
    <DataTable responsive>
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Connection</DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden sm:table-cell">
            Method
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden md:table-cell">
            Access
          </DataTable.HeaderCell>
          <DataTable.HeaderCell>Status</DataTable.HeaderCell>
          <DataTable.HeaderCell>
            <span className="sr-only">Actions</span>
          </DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {rows.map((row) => (
          <DataTable.Row key={`${row.kind}:${row.id}`}>
            <DataTable.Cell>{row.name}</DataTable.Cell>
            <DataTable.Cell className="hidden sm:table-cell">
              {row.method}
            </DataTable.Cell>
            <DataTable.Cell className="hidden md:table-cell">
              {row.access}
            </DataTable.Cell>
            <DataTable.Cell>
              <Chip tone={STATUS_TONES[row.tone]}>{row.status}</Chip>
            </DataTable.Cell>
            <DataTable.Cell className="text-right">
              {!pending && (
                <Button
                  variant="cancel"
                  size="xs"
                  onClick={() => onRevoke(row)}
                  loading={revokingId === row.id}
                  loadingText="Revoking..."
                >
                  Revoke
                </Button>
              )}
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    </DataTable>
  );
}

export function SkillMeshConnectionsPanel({
  clientLabel,
  hasLegacyTokens,
  onRevoke,
  pendingRow,
  revokingId,
  rows,
}: {
  clientLabel: string;
  hasLegacyTokens: boolean;
  onRevoke: (row: SkillMeshConnectionRow) => void;
  pendingRow: SkillMeshConnectionRow;
  revokingId: string | null;
  rows: SkillMeshConnectionRow[];
}) {
  const pending = rows.length === 0;
  return (
    <PanelSection
      title="03 · Check it works"
      badge={<span>{rows.length}</span>}
    >
      <div className="space-y-4">
        <p className={BODY_SM}>
          Ask {clientLabel}:{" "}
          <span className="font-mono text-emerald-600 dark:text-emerald-300">
            “What skills are in Skill Mesh?”
          </span>{" "}
          The connection shows as active after its first request.
        </p>
        <ConnectionsTable
          onRevoke={onRevoke}
          pending={pending}
          revokingId={revokingId}
          rows={pending ? [pendingRow] : rows}
        />
        {hasLegacyTokens && (
          <Alert variant="warning" title="Replace legacy combined tokens">
            Tokens that combine ARTI and Skill Mesh permissions no longer
            authenticate. Revoke them and create a Skill Mesh token here.
          </Alert>
        )}
      </div>
    </PanelSection>
  );
}
